import type { LoggerService } from '@/services/abstract/LoggerService';
import type { CidDownloadLogger } from '@/services/abstract/poc/CidDownloadLogger';
import type { FileEncryptionService } from '@/services/abstract/poc/fileEncryption.service.interface';
import type { MessagingService } from '@/services/abstract/poc/MessagingService';
import { CryptoServiceImpl } from '@/services/poc/CryptoServiceImpl';
import { CreateSixServiceImpl } from '@/services/createSix/CreateSixServiceImpl';
import { DiagnosticsServiceImpl } from '@/services/diagnostics/DiagnosticsServiceImpl';
import { FileKeyFacilitationServiceImpl } from '@/services/fileKey/FileKeyFacilitationServiceImpl';
import { LoggerServiceImpl } from '@/services/logger/LoggerServiceImpl';
import { OrganizationServiceImpl } from '@/services/organization/OrganizationServiceImpl';
import { CidDownloadLoggerImpl } from '@/services/poc/CidDownloadLoggerImpl';
import { FileDownloadServiceImpl } from '@/services/poc/FileDownloadServiceImpl';
import { FileEncryptionServiceImpl } from '@/services/poc/FileEncryptionServiceImpl';
import { FileStoreServiceImpl } from '@/services/poc/FileStoreServiceImpl';
import { HeliaLifecycleServiceImpl } from '@/services/poc/HeliaLifecycleServiceImpl';
import { Libp2pServiceImpl } from '@/services/poc/Libp2pServiceImpl';
import { MessagingServiceImpl } from '@/services/poc/MessagingServiceImpl';
import { P2PStackManagerImpl } from '@/services/poc/P2PStackManagerImpl';
import { MlkemKeysServiceImpl } from '@/services/poc/MlkemKeysServiceImpl';
import { createUploadSuccessfulHandler } from '@/services/poc/handlers/uploadSuccessfulHandler';
import { createFileReplicationProgressHandler } from '@/services/poc/handlers/fileReplicationProgressHandler';
import { createFileReplicationStartedHandler } from '@/services/poc/handlers/fileReplicationStartedHandler';
import { createFileReplicationFailedHandler } from '@/services/poc/handlers/fileReplicationFailedHandler';
import { SixCatalogServiceImpl } from '@/services/sixCatalog/SixCatalogServiceImpl';
import { SixDetailServiceImpl } from '@/services/sixDetail/SixDetailServiceImpl';
import { UserServiceImpl } from '@/services/user/UserServiceImpl';
import type { AppServices, PocServices } from '@/types/services.types';
import { HttpFetcherService } from '@/services/http/HttpFetcherService';
import { AuthServiceImpl } from '@/services/auth/AuthServiceImpl';

function createPocServices(
  loggerService: LoggerService,
  httpFetcherService: HttpFetcherService
): PocServices {
  const cryptoService = new CryptoServiceImpl(loggerService);
  const libp2pService = new Libp2pServiceImpl(loggerService);
  const heliaLifecycleService = new HeliaLifecycleServiceImpl(loggerService);
  const fileEncryptionService: FileEncryptionService = new FileEncryptionServiceImpl(
    cryptoService,
    loggerService
  );
  const cidDownloadLogger: CidDownloadLogger = new CidDownloadLoggerImpl(loggerService);
  const fileStoreService = new FileStoreServiceImpl(loggerService, cidDownloadLogger);
  const fileDownloadService = new FileDownloadServiceImpl();
  const messagingService: MessagingService = new MessagingServiceImpl(
    loggerService,
    libp2pService,
    [
      createUploadSuccessfulHandler(loggerService),
      createFileReplicationStartedHandler(loggerService),
      createFileReplicationProgressHandler(loggerService),
      createFileReplicationFailedHandler(loggerService),
    ]
  );

  return {
    libp2pService,
    heliaLifecycleService,
    fileStoreService,
    fileDownloadService,
    p2pStackManagerService: new P2PStackManagerImpl(
      loggerService,
      libp2pService,
      heliaLifecycleService,
      messagingService
    ),
    messagingService,
    mlkemKeysService: new MlkemKeysServiceImpl(httpFetcherService),
    cryptoService,
    fileEncryptionService,
    cidDownloadLogger,
  };
}

export const createJsonServices = (): AppServices => {
  const loggerService = new LoggerServiceImpl();
  const httpFetcherService = new HttpFetcherService();
  return {
    authService: new AuthServiceImpl(httpFetcherService),
    userService: new UserServiceImpl(httpFetcherService),
    sixDetailService: new SixDetailServiceImpl(httpFetcherService),
    createSixService: new CreateSixServiceImpl(httpFetcherService),
    sixCatalogService: new SixCatalogServiceImpl(httpFetcherService),
    loggerService,
    organizationService: new OrganizationServiceImpl(httpFetcherService),
    fileKeyFacilitationService: new FileKeyFacilitationServiceImpl(httpFetcherService),
    diagnosticsService: new DiagnosticsServiceImpl(),
    pocServices: createPocServices(loggerService, httpFetcherService),
  };
};
